import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../api';
import { useDataCache } from '../DataCacheContext';
import InteractiveBoard from './InteractiveBoard';
import useStockfish from '../hooks/useStockfish';

// ── Move classification thresholds (centipawns) ──
const THRESHOLDS = {
  brilliant: -50,   // player improved position far beyond best (unlikely but possible with sacrifices)
  great: 0,         // at or better than engine best
  good: -10,        // within 10cp of best
  inaccuracy: -50,  // 10-50cp loss
  mistake: -150,    // 50-150cp loss
  blunder: -300,    // 150-300cp loss
};

function classifyMove(evalDrop) {
  // evalDrop = (eval after player's move) - (best eval before move), from player's perspective
  // Negative means the player lost advantage
  if (evalDrop >= THRESHOLDS.great) return 'great';
  if (evalDrop >= THRESHOLDS.good) return 'good';
  if (evalDrop >= THRESHOLDS.inaccuracy) return 'inaccuracy';
  if (evalDrop >= THRESHOLDS.mistake) return 'mistake';
  if (evalDrop >= THRESHOLDS.blunder) return 'blunder';
  return 'blunder';
}

const CLASSIFICATION_COLORS = {
  brilliant: '#FF9500',
  great: '#00A5C4',
  good: '#00A5C4',
  inaccuracy: '#FFD700',
  mistake: '#DE7345',
  blunder: '#E4446E',
  book: '#8A74E4',
};

const CLASSIFICATION_ICONS = {
  brilliant: '\u2605',
  great: '\u2713',
  good: '\u00B7',
  inaccuracy: '?!',
  mistake: '?',
  blunder: '??',
  book: '\u266A',
};

export default function GameAnalysisPanel({ c, pieceSet, boardTheme, setPieceSet, setBoardTheme }) {
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username') || '';
  const cache = useDataCache();

  // ── Game list state ──
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  // ── Analysis state ──
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [moveEvals, setMoveEvals] = useState([]); // { fen, san, cp, bestCp, bestMove, classification, moveNumber, color }
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // ── Board state ──
  const [moveIndex, setMoveIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [hoveredSquare, setHoveredSquare] = useState(null);

  // ── Stockfish (browser-side) ──
  const { evaluatePosition: sfEvaluate, stop: sfStop, ready: sfReady } = useStockfish(18);
  const abortRef = useRef(false);

  // ── Fetch games list ──
  useEffect(() => {
    if (!username) { setLoadingGames(false); return; }
    const cacheKey = `games-list:${username}`;
    const cached = cache.get(cacheKey);
    if (cached) { setGames(cached); setLoadingGames(false); return; }

    fetch(`${API_BASE}/games?username=${encodeURIComponent(username)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        // Sort by date descending
        const sorted = data.sort((a, b) => (b.game_date_utc || '').localeCompare(a.game_date_utc || ''));
        cache.set(cacheKey, sorted, 300);
        setGames(sorted);
        setLoadingGames(false);
      })
      .catch(() => setLoadingGames(false));
  }, [username, cache]);

  // ── Parse selected game into positions ──
  const gameData = useMemo(() => {
    if (!selectedGame) return null;
    const chess = new Chess();
    const movesRaw = selectedGame.moves || '';
    // Clean PGN: remove move numbers, result, clock annotations
    const tokens = movesRaw
      .replace(/\{[^}]*\}/g, '')
      .replace(/\d+\.\.\./g, '')
      .replace(/\d+\./g, '')
      .replace(/(1-0|0-1|1\/2-1\/2|\*)$/g, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    const positions = [chess.fen()];
    const moves = [];
    for (const san of tokens) {
      try {
        const result = chess.move(san);
        if (result) {
          moves.push({ san: result.san, from: result.from, to: result.to, fen: chess.fen(), color: result.color });
          positions.push(chess.fen());
        }
      } catch { break; }
    }
    return { positions, moves };
  }, [selectedGame]);

  // ── Current board state ──
  const currentFen = gameData ? (gameData.positions[moveIndex] || gameData.positions[0]) : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);
  const board = boardChess.board();

  // ── Navigation ──
  const goTo = useCallback((idx) => {
    if (!gameData) return;
    const clamped = Math.max(0, Math.min(idx, gameData.positions.length - 1));
    setMoveIndex(clamped);
    if (clamped > 0 && gameData.moves[clamped - 1]) {
      const m = gameData.moves[clamped - 1];
      setLastMove({ from: m.from, to: m.to });
    } else {
      setLastMove(null);
    }
  }, [gameData]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(moveIndex - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(moveIndex + 1); }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(gameData ? gameData.positions.length - 1 : 0); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [moveIndex, goTo, gameData]);

  // ── Show final position when selecting a game (before analysis) ──
  useEffect(() => {
    if (gameData && !analysisComplete && !analyzing && moveEvals.length === 0) {
      setMoveIndex(gameData.positions.length - 1);
    }
  }, [gameData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run analysis ──
  const startAnalysis = useCallback(async () => {
    if (!gameData || !sfReady) return;
    abortRef.current = false;
    setAnalyzing(true);
    setAnalysisComplete(false);
    setMoveEvals([]);
    setAnalysisProgress(0);
    setMoveIndex(0);

    const evals = [];
    const totalMoves = gameData.moves.length;

    // Evaluate starting position
    await sfEvaluate(gameData.positions[0]);

    for (let i = 0; i < totalMoves; i++) {
      if (abortRef.current) break;

      const move = gameData.moves[i];
      const fenAfter = gameData.positions[i + 1];

      // Evaluate position after this move
      const result = await sfEvaluate(fenAfter);
      if (abortRef.current) break;
      if (!result || result.cp === null) continue;

      // cp from engine is side-to-move perspective
      // Convert to white's perspective
      let cpAfter = result.cp;
      if (fenAfter.includes(' b ')) cpAfter = -cpAfter; // if black to move, negate

      // Best move from previous position
      const fenBefore = gameData.positions[i];
      const bestResult = await sfEvaluate(fenBefore);
      if (abortRef.current) break;

      let bestCp = bestResult?.cp || 0;
      // Convert best eval to white's perspective
      if (fenBefore.includes(' b ')) bestCp = -bestCp;

      let bestMoveUci = bestResult?.bestMoveUci || '';
      let bestMoveSan = bestMoveUci;
      try {
        const tmp = new Chess(fenBefore);
        const mo = tmp.move({ from: bestMoveUci.slice(0, 2), to: bestMoveUci.slice(2, 4), promotion: bestMoveUci[4] || undefined });
        if (mo) bestMoveSan = mo.san;
      } catch {}

      // Eval drop from the moving player's perspective
      // If white moved: drop = cpAfter - bestCp (both from white's perspective)
      // If black moved: drop = -(cpAfter - bestCp) ... actually:
      // For the player who moved, a good move means their side's eval stayed high
      let evalDrop;
      if (move.color === 'w') {
        evalDrop = cpAfter - bestCp; // positive = player did as well or better than best
      } else {
        evalDrop = -(cpAfter - bestCp); // for black, improvement means white's eval decreased
      }

      const classification = classifyMove(evalDrop);

      evals.push({
        index: i,
        fen: fenAfter,
        san: move.san,
        from: move.from,
        to: move.to,
        color: move.color,
        cpAfter,
        bestCp,
        bestMove: bestMoveSan,
        bestMoveUci,
        evalDrop,
        classification,
        moveNumber: Math.floor(i / 2) + 1,
      });

      setMoveEvals([...evals]);
      setAnalysisProgress(Math.round(((i + 1) / totalMoves) * 100));
      setMoveIndex(i + 1);
    }

    if (!abortRef.current) {
      setAnalysisComplete(true);
    }
    setAnalyzing(false);
  }, [gameData, sfReady, sfEvaluate]);

  // ── Accuracy calculation ──
  const accuracy = useMemo(() => {
    if (!moveEvals.length || !analysisComplete) return null;
    const calc = (color) => {
      const moves = moveEvals.filter(m => m.color === color);
      if (!moves.length) return 0;
      // Accuracy = average of per-move accuracy, where each move's accuracy is clamped sigmoid
      const scores = moves.map(m => {
        const drop = Math.min(0, m.evalDrop); // only penalize losses
        return Math.max(0, 100 * Math.exp(drop / 100)); // exponential decay
      });
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    };
    return { white: calc('w'), black: calc('b') };
  }, [moveEvals, analysisComplete]);

  // ── Summary counts ──
  const summary = useMemo(() => {
    if (!moveEvals.length) return null;
    const counts = { w: {}, b: {} };
    for (const m of moveEvals) {
      counts[m.color][m.classification] = (counts[m.color][m.classification] || 0) + 1;
    }
    return counts;
  }, [moveEvals]);

  // ── Eval bar ──
  const evalPercent = useMemo(() => {
    if (!moveEvals.length || moveIndex === 0) return 50;
    const evalEntry = moveEvals.find(m => m.index === moveIndex - 1);
    if (!evalEntry) return 50;
    const cp = flipped ? -evalEntry.cpAfter : evalEntry.cpAfter;
    const sigmoid = 50 + 50 * (2 / (1 + Math.exp(-cp / 300)) - 1);
    return Math.max(2, Math.min(98, Math.round(sigmoid)));
  }, [moveEvals, moveIndex, flipped]);

  const evalDisplay = useMemo(() => {
    if (!moveEvals.length || moveIndex === 0) return '0.0';
    const evalEntry = moveEvals.find(m => m.index === moveIndex - 1);
    if (!evalEntry) return '0.0';
    const cp = flipped ? -evalEntry.cpAfter : evalEntry.cpAfter;
    if (Math.abs(cp) >= 10000) return cp > 0 ? 'M' : '-M';
    return (cp / 100).toFixed(1);
  }, [moveEvals, moveIndex, flipped]);

  // ── Eval graph data (minimap) ──
  const evalGraphPoints = useMemo(() => {
    if (!moveEvals.length) return '';
    const width = 300;
    const height = 60;
    const points = moveEvals.map((m, i) => {
      const x = (i / (moveEvals.length - 1 || 1)) * width;
      const cp = Math.max(-500, Math.min(500, m.cpAfter));
      const y = height / 2 - (cp / 500) * (height / 2);
      return `${x},${y}`;
    });
    return points.join(' ');
  }, [moveEvals]);

  // ── Best move arrow ──
  const bestMoveArrow = useMemo(() => {
    if (!analysisComplete || moveIndex === 0 || !moveEvals.length) return null;
    const evalEntry = moveEvals.find(m => m.index === moveIndex - 1);
    if (!evalEntry || !evalEntry.bestMoveUci || evalEntry.bestMoveUci.length < 4) return null;
    if (evalEntry.classification === 'great' || evalEntry.classification === 'good') return null;
    return { from: evalEntry.bestMoveUci.slice(0, 2), to: evalEntry.bestMoveUci.slice(2, 4), color: '#5CB85C' };
  }, [moveEvals, moveIndex, analysisComplete]);

  // ── Current move classification ──
  const currentMoveEval = moveIndex > 0 ? moveEvals.find(m => m.index === moveIndex - 1) : null;

  // Auto-select latest game when games load and none is selected
  useEffect(() => {
    if (!selectedGame && games.length > 0) {
      const g = games[0];
      const isW = (g.white_player_username || '').toLowerCase() === username.toLowerCase();
      setSelectedGame(g);
      setFlipped(!isW);
    }
  }, [games, selectedGame, username]);

  // ── Analysis view ──
  const isWhite = selectedGame ? (selectedGame.white_player_username || '').toLowerCase() === username.toLowerCase() : true;
  const cardSx = { px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10` };

  // ── Game list panel (reused in left column) ──
  const gameListPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Game Analysis</Typography>
      <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 2 }}>
        Select a recent game to analyze move by move with Stockfish.
      </Typography>
      {loadingGames ? (
        <Typography sx={{ color: c.textMuted, py: 4 }}>Loading your games...</Typography>
      ) : games.length === 0 ? (
        <Typography sx={{ color: c.textMuted, py: 4 }}>No games found for {username}.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 520, overflowY: 'auto', pr: 2, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: `${c.textMuted}30`, borderRadius: 2 } }}>
          {games.slice(0, 30).map((game, i) => {
            const isW = (game.white_player_username || '').toLowerCase() === username.toLowerCase();
            const opponent = isW ? game.black_player_username : game.white_player_username;
            const opponentElo = isW ? game.black_player_elo : game.white_player_elo;
            const resultText = game.outcome === 'win' ? 'Won' : game.outcome === 'loss' ? 'Lost' : 'Draw';
            const resultColor = game.outcome === 'win' ? '#00A5C4' : game.outcome === 'loss' ? '#E4446E' : c.textMuted;
            const isSelected = selectedGame && game.game_url === selectedGame.game_url;
            return (
              <Box
                key={i}
                onClick={() => { setSelectedGame(game); setMoveEvals([]); setAnalysisComplete(false); setFlipped(!isW); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2,
                  borderRadius: '12px', cursor: 'pointer',
                  bgcolor: isSelected ? `${c.accent}12` : `${c.textMuted}06`,
                  border: `1px solid ${isSelected ? `${c.accent}30` : `${c.textMuted}10`}`,
                  '&:hover': { bgcolor: `${c.accent}08`, borderColor: `${c.accent}25` },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: isW ? '#ECE2DB' : '#111115', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>vs {opponent}</Typography>
                    <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>({opponentElo})</Typography>
                    <Typography sx={{ color: resultColor, fontSize: '0.88rem', fontWeight: 700, ml: 'auto' }}>{resultText}</Typography>
                  </Box>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.82rem', mt: 0.2 }}>{game.opening_name || 'Unknown'} · {game.game_type} · {game.game_date_utc?.slice(0, 10)}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>
      {/* ── Left: Game list ── */}
      <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 280px' }, maxWidth: { xs: '100%', lg: 280 }, minWidth: 0 }}>
        {gameListPanel}
      </Box>

      {/* ── Center: Board + Right: Analysis ── */}
      {selectedGame && <><Box sx={{ flexShrink: 0 }}>
        <InteractiveBoard
          c={c}
          board={board}
          fen={currentFen}
          flipped={flipped}
          pieceSet={pieceSet}
          boardTheme={boardTheme}
          selectedSquare={null}
          legalMoves={[]}
          lastMove={lastMove}
          onSquareClick={() => {}}
          onDragStart={() => {}}
          onFlip={() => setFlipped(f => !f)}
          onGoTo={goTo}
          setArrows={setArrows}
          setHighlightedSquares={setHighlightedSquares}
          setPieceSet={setPieceSet}
          setBoardTheme={setBoardTheme}
          arrows={[...(bestMoveArrow ? [bestMoveArrow] : []), ...arrows]}
          highlightedSquares={highlightedSquares}
          hoveredSquare={hoveredSquare}
          setHoveredSquare={setHoveredSquare}
          positions={gameData?.positions || []}
          moveIndex={moveIndex}
          showEvalBar={analysisComplete || analyzing}
          evalPercent={evalPercent}
          materialAdvantage={evalDisplay}
        />
      </Box>

      {/* ── Right: Analysis panel ── */}
      <Box sx={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>

        {/* Back button + game info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            onClick={() => { setSelectedGame(null); abortRef.current = true; sfStop(); }}
            sx={{ cursor: 'pointer', color: c.textMuted, '&:hover': { color: c.accent }, transition: 'color 0.15s' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </Box>
          <Box>
            <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 700 }}>
              vs {isWhite ? selectedGame.black_player_username : selectedGame.white_player_username}
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>
              {selectedGame.opening_name || 'Unknown'} &middot; {selectedGame.game_type} &middot; {selectedGame.game_date_utc?.slice(0, 10)}
            </Typography>
          </Box>
        </Box>

        {/* Analyze button */}
        {!analysisComplete && !analyzing && (
          <Button
            onClick={startAnalysis}
            disabled={!sfReady}
            sx={{
              bgcolor: c.blue, color: '#fff', fontWeight: 700, fontSize: '0.95rem',
              borderRadius: '14px', py: 1.5, textTransform: 'none',
              '&:hover': { bgcolor: `${c.blue}dd` },
              '&:disabled': { bgcolor: `${c.textMuted}30`, color: c.textMuted },
            }}
          >
            {sfReady ? 'Analyze Game with Stockfish' : 'Loading Stockfish...'}
          </Button>
        )}

        {/* Analysis progress */}
        {analyzing && (
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>Analyzing...</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{analysisProgress}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={analysisProgress}
              sx={{
                height: 6, borderRadius: 3,
                bgcolor: `${c.textMuted}15`,
                '& .MuiLinearProgress-bar': { bgcolor: c.blue, borderRadius: 3 },
              }}
            />
            <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 1 }}>
              Move {moveEvals.length} / {gameData?.moves.length || 0}
            </Typography>
          </Box>
        )}

        {/* Accuracy summary */}
        {analysisComplete && accuracy && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ ...cardSx, flex: 1, textAlign: 'center' }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#ECE2DB', mx: 'auto', mb: 0.8 }} />
              <Typography sx={{ color: c.textDark, fontSize: '1.5rem', fontWeight: 800 }}>{accuracy.white}%</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>Accuracy</Typography>
            </Box>
            <Box sx={{ ...cardSx, flex: 1, textAlign: 'center' }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#111115', mx: 'auto', mb: 0.8 }} />
              <Typography sx={{ color: c.textDark, fontSize: '1.5rem', fontWeight: 800 }}>{accuracy.black}%</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>Accuracy</Typography>
            </Box>
          </Box>
        )}

        {/* Classification summary */}
        {analysisComplete && summary && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {['w', 'b'].map(color => (
              <Box key={color} sx={{ ...cardSx, flex: 1 }}>
                {['great', 'good', 'inaccuracy', 'mistake', 'blunder'].map(cls => (
                  <Box key={cls} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CLASSIFICATION_COLORS[cls] }} />
                      <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', textTransform: 'capitalize' }}>{cls}</Typography>
                    </Box>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>{summary[color][cls] || 0}</Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}

        {/* Eval graph */}
        {moveEvals.length > 1 && (
          <Box sx={{ ...cardSx, py: 1.5 }}>
            <svg
              width="100%"
              viewBox="0 0 300 60"
              preserveAspectRatio="none"
              style={{ display: 'block', cursor: 'pointer' }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = (e.clientX - rect.left) / rect.width;
                const idx = Math.round(x * (moveEvals.length - 1));
                goTo(moveEvals[idx]?.index + 1 || 0);
              }}
            >
              {/* Zero line */}
              <line x1="0" y1="30" x2="300" y2="30" stroke={`${c.textMuted}30`} strokeWidth="0.5" />
              {/* Eval fill - white advantage (below center) */}
              <polyline
                points={`0,30 ${evalGraphPoints} 300,30`}
                fill={`${c.textMuted}12`}
                stroke="none"
              />
              {/* Eval line */}
              <polyline
                points={evalGraphPoints}
                fill="none"
                stroke={c.blue}
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
              {/* Current position indicator */}
              {moveIndex > 0 && moveEvals.length > 0 && (() => {
                const idx = Math.min(moveIndex - 1, moveEvals.length - 1);
                const x = (idx / (moveEvals.length - 1 || 1)) * 300;
                return <line x1={x} y1="0" x2={x} y2="60" stroke={c.accent} strokeWidth="1.5" opacity="0.6" />;
              })()}
            </svg>
          </Box>
        )}

        {/* Current move info */}
        {currentMoveEval && (
          <Box sx={{
            ...cardSx,
            bgcolor: `${CLASSIFICATION_COLORS[currentMoveEval.classification]}08`,
            border: `1px solid ${CLASSIFICATION_COLORS[currentMoveEval.classification]}25`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: CLASSIFICATION_COLORS[currentMoveEval.classification],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: '0.75rem', fontWeight: 800,
              }}>
                {CLASSIFICATION_ICONS[currentMoveEval.classification]}
              </Box>
              <Typography sx={{ color: c.textDark, fontSize: '1rem', fontWeight: 700, textTransform: 'capitalize' }}>
                {currentMoveEval.classification}
              </Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.9rem', ml: 'auto' }}>
                {currentMoveEval.moveNumber}.{currentMoveEval.color === 'b' ? '..' : ''} {currentMoveEval.san}
              </Typography>
            </Box>
            {(currentMoveEval.classification === 'inaccuracy' || currentMoveEval.classification === 'mistake' || currentMoveEval.classification === 'blunder') && (
              <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                Best was <strong style={{ color: '#5CB85C' }}>{currentMoveEval.bestMove}</strong>
                {' '}({(currentMoveEval.evalDrop / 100).toFixed(1)} pawns lost)
              </Typography>
            )}
          </Box>
        )}

        {/* Move list */}
        {gameData && (
          <Box sx={{ ...cardSx, maxHeight: 280, overflowY: 'auto', py: 1.5, px: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center' }}>
              {gameData.moves.map((move, i) => {
                const evalEntry = moveEvals.find(m => m.index === i);
                const isActive = moveIndex === i + 1;
                const cls = evalEntry?.classification;
                const showNumber = i % 2 === 0;
                return (
                  <React.Fragment key={i}>
                    {showNumber && (
                      <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', fontWeight: 600, minWidth: 22, textAlign: 'right', mr: 0.3, userSelect: 'none' }}>
                        {Math.floor(i / 2) + 1}.
                      </Typography>
                    )}
                    <Box
                      onClick={() => goTo(i + 1)}
                      sx={{
                        px: 0.8, py: 0.3, borderRadius: '6px', cursor: 'pointer', userSelect: 'none',
                        bgcolor: isActive ? `${c.accent}20` : 'transparent',
                        '&:hover': { bgcolor: isActive ? `${c.accent}30` : `${c.textMuted}10` },
                        display: 'flex', alignItems: 'center', gap: 0.4,
                      }}
                    >
                      <Typography sx={{ color: isActive ? c.textDark : c.textMuted, fontSize: '0.82rem', fontWeight: isActive ? 700 : 400 }}>
                        {move.san}
                      </Typography>
                      {cls && (cls === 'inaccuracy' || cls === 'mistake' || cls === 'blunder') && (
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: CLASSIFICATION_COLORS[cls],
                          flexShrink: 0,
                        }} />
                      )}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        )}

      </Box></>}
    </Box>
  );
}
